import { lodash } from '@avantia/lodash';
import { createControlId } from '@avantia/questionset-model';
import { HpSvgIconTypes, SvgIcon } from '@avantia/style-guide';
import { KeyboardEventHandler, MouseEventHandler } from 'react';
import { triggerNavigationBarEvent } from '../../../actions/eventActions';
import { HpNavigationBarElementTypes } from '../../../reducers/questionSetTools';
import { updateDocumentTitle } from '../../../tools/formatting';
import { HpEventDetail, HpNavTabEventDetail, HpOnMouseEvent } from '../../../types';
import { HpAdditionalKeyboardHandler, createSpaceKeyAsClickHandler } from '../controlEvents';
import {
  HpCreateItemProps,
  HpCreateTabItems,
  HpDetermineStatusProps,
  HpGetPercentCompletedProps,
  HpGetPercentCompletedResult,
  HpHasFlagFunction,
  HpNavigationMenuType,
  HpNavigationTabProps,
  HpStatus
} from './navigationTabControlTypes';

export const setDocumentTitle = createTitleHandler();

export function createTabItems(props: HpCreateTabItems): JSX.Element[] {
  const { eventDetail, hasError, highestTouchedIndex, isJourneyComplete, items, menuClose, menuType, onClick } = props;
  let { activeIndex } = props;
  if (!items || items.length === 0) {
    return [];
  }

  let hasActive = true;
  if (activeIndex < 0) {
    activeIndex = 0;
    hasActive = false;
  }

  return items.map((item, index) => {
    const status = determineStatus({
      index,
      activeIndex,
      highestTouchedIndex,
      isJourneyComplete,
      isNavigationAllowed: item.isNavigationAllowed,
      hasActive,
      hasError
    });
    const hasFlag: HpHasFlagFunction = (y) => (status & y) === y;
    const type = getIconType({ hasFlag, menuType });
    const { key: id, onClick: onItemClick } = item;
    const clickHandler = getClickHandler(hasFlag, onClick, onItemClick, id, eventDetail);
    return createItem({ item, index, type, menuType, clickHandler, hasFlag, menuClose });
  });
}

function createItem({ clickHandler, hasFlag, index, item, menuClose, menuType, type }: HpCreateItemProps): JSX.Element {
  const { key: id, name } = item;
  const className = iterateFlags(hasFlag, (name) => {
    return `av-${name === 'afterCurrent' ? 'after-current' : name === 'highestTouched' ? 'highest-touched' : name}`;
  });

  let content: JSX.Element;
  let tabIndex: number | undefined = clickHandler ? 0 : -1;

  if (menuType === 'submenu') {
    content = (
      <div className="dropdown-item" tabIndex={tabIndex}>
        <div></div>
        <div>{index + 1}</div>
        <div>{name}</div>
        <div>
          <SvgIcon type={statusToSvgType(hasFlag)} />
        </div>
      </div>
    );
    tabIndex = undefined;
  } else {
    content = (
      <>
        <span className="av-timeline-icon">{type && <SvgIcon type={type} size="auto" />}</span>
        <label>
          <span>{name}</span>
        </label>
      </>
    );
  }

  let onKeyDown: KeyboardEventHandler | undefined;
  if (clickHandler) {
    let additionalHandler: HpAdditionalKeyboardHandler | undefined;
    if (menuClose) {
      additionalHandler = (key, event) => {
        if (key === 'Tab') {
          const ev = { ...event }; // copy it or we will get an error.
          setTimeout(() => {
            const active = document.activeElement;
            if (active) {
              const isMenu = jQuery(active).closest('.av-timeline-submenu').length > 0;
              if (!isMenu) {
                menuClose(ev as any);
              }
            }
          }, 50);
        }
      };
    }

    onKeyDown = createSpaceKeyAsClickHandler(clickHandler, additionalHandler);
  } else {
    clickHandler = (event) => {
      const className = 'hp-action-disabled';
      const li = jQuery(event.target).closest('li');
      li.addClass(className);
      triggerNavigationBarEvent({
        getEvent: () => event,
        actionType: 'not-allowed',
        elementType: (menuType as HpNavigationBarElementTypes) || 'menu'
      });
      setTimeout(() => li.removeClass(className), 600);
    };
  }

  return (
    <li
      id={createControlId({ questionId: id })}
      key={id}
      title={name}
      className={className}
      // eventKey={id}
      // disabled={hasError && activeKey !== id}
      onClick={clickHandler}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
    >
      {content}
    </li>
  );
}

export function getPercentCompleted({
  activeKey,
  highestTouchedKey,
  items
}: HpGetPercentCompletedProps): HpGetPercentCompletedResult {
  const count = items.length;
  const activeIndex = lodash.findIndex(items, (i) => i.key === activeKey);

  let highestTouchedIndex = lodash.findIndex(items, (i) => i.key === highestTouchedKey);
  if (highestTouchedIndex < 0) {
    highestTouchedIndex = 0;
  }

  const percentCompleted = (100.0 / (count - 1)) * (activeIndex < 0 ? 0 : activeIndex);

  return { highestTouchedIndex, percentCompleted, activeIndex };
}

function createTitleHandler(): (activeItem: HpNavigationTabProps) => void {
  return function (activeItem: HpNavigationTabProps): void {
    if (activeItem) {
      updateDocumentTitle(activeItem);
    }
  };
}

function statusToSvgType(hasFlag: HpHasFlagFunction): HpSvgIconTypes {
  if (hasFlag(HpStatus.error)) {
    return 'cross';
  } else if (hasFlag(HpStatus.complete) || hasFlag(HpStatus.current) || hasFlag(HpStatus.highestTouched)) {
    return 'check';
  }

  return 'cross-circle-white' as HpSvgIconTypes; // it's a custom SVG.
}

function determineStatus({
  activeIndex,
  hasActive,
  hasError,
  highestTouchedIndex,
  index,
  isJourneyComplete,
  isNavigationAllowed = true
}: HpDetermineStatusProps): HpStatus {
  let status: HpStatus = HpStatus.none;
  if (hasActive && index === activeIndex) {
    status |= HpStatus.current;
    if (hasError) {
      status |= HpStatus.error;
    }
  } else if (!isNavigationAllowed) {
    return HpStatus.unstarted;
  }

  if (index < highestTouchedIndex || isJourneyComplete) {
    status |= HpStatus.complete;
  }

  if (!isJourneyComplete) {
    if (index > activeIndex) {
      status |= HpStatus.afterCurrent;
    }

    if (index === highestTouchedIndex) {
      status |= HpStatus.highestTouched;
    }

    if (index > highestTouchedIndex) {
      status |= HpStatus.unstarted;
    }
  }

  return status;
}

function iterateFlags(hasFlag: HpHasFlagFunction, func: (name: string) => string): string {
  return lodash
    .keys(HpStatus)
    .filter((key) => {
      const val = HpStatus[key];
      return val > 0 && hasFlag(val);
    })
    .map((key) => {
      return func(key);
    })
    .join(' ');
}

interface HpGetIconTypeProps {
  hasFlag: HpHasFlagFunction;
  menuType: HpNavigationMenuType;
}

function getIconType({ hasFlag, menuType }: HpGetIconTypeProps): HpSvgIconTypes | undefined {
  if (menuType === 'menu') {
    if (hasFlag(HpStatus.afterCurrent)) {
      return undefined;
    } else if (hasFlag(HpStatus.current)) {
      return 'angle-down';
    } else if (hasFlag(HpStatus.complete)) {
      return 'angle-right';
    }

    return undefined;
  }

  let type: HpSvgIconTypes | undefined;
  if (hasFlag(HpStatus.complete)) {
    type = 'check';
  } else if (hasFlag(HpStatus.current)) {
    type = 'angle-down';
  }

  if (hasFlag(HpStatus.complete | HpStatus.afterCurrent)) {
    type = 'check';
  } else if (hasFlag(HpStatus.highestTouched)) {
    type = 'check';
  }

  return type;
}

function getClickHandler(
  hasFlag: HpHasFlagFunction,
  onClick: HpOnMouseEvent | null,
  itemClick: MouseEventHandler<HTMLLIElement> | undefined,
  id: string,
  eventDetail: HpEventDetail
): MouseEventHandler<HTMLLIElement> | undefined {
  if (hasFlag(HpStatus.unstarted)) {
    return undefined;
  }

  let clickHandler: MouseEventHandler<HTMLLIElement> | undefined;
  if (onClick) {
    clickHandler = (e: React.MouseEvent<HTMLElement, MouseEvent>): void =>
      onClick(e, { ...eventDetail, activeKey: id } as HpNavTabEventDetail);
  } else if (itemClick) {
    clickHandler = itemClick;
  }

  return clickHandler;
}
